import React, { useState, useEffect } from 'react';
import { entitiesApi, entityBankAccountsApi, subCategoriesApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Edit2, Trash2, Users, Building, Handshake, UserCheck, Phone, Mail, MapPin, CreditCard, X, Landmark, PlusCircle, Tag } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { CATEGORIES, SUB_CAT_CATEGORIES, SUB_CAT_LABEL, CATEGORY_DOT_COLOR, getEntityTypeForCategory } from '@/constants/categories';

const ENTITY_TYPES = [
  { value: 'person', label: 'Person / Labour', icon: Users },
  { value: 'vendor', label: 'Vendor / Agency', icon: Building },
  { value: 'partner', label: 'Partner', icon: Handshake },
  { value: 'customer', label: 'Customer', icon: UserCheck },
];

export default function EntitiesPage() {
  const { user } = useAuth();
  const isWriter = ['admin', 'manager'].includes(user?.role);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('vendor');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', entity_type: 'vendor', phone: '', email: '', address: '', category: '', sub_category: '', supplies: [] });
  const [subCategories, setSubCategories] = useState([]);

  // Inline bank accounts during creation/editing
  const [formBankAccounts, setFormBankAccounts] = useState([]);
  const [newBankAcc, setNewBankAcc] = useState({ account_name: '', bank_name: '', account_number: '', ifsc_code: '' });

  // Bank accounts dialog for existing entity
  const [showBankAccounts, setShowBankAccounts] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [dialogNewAcc, setDialogNewAcc] = useState({ account_name: '', bank_name: '', account_number: '', ifsc_code: '' });

  const fetchEntities = async () => {
    setLoading(true);
    try {
      const { data } = await entitiesApi.list();
      setEntities(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    fetchEntities();
    subCategoriesApi.list().then(r => setSubCategories(r.data)).catch(() => {});
  }, []);

  // Supply search state
  const [supplySearch, setSupplySearch] = useState('');

  const filteredEntities = entities.filter(e => e.entity_type === tab);
  const filteredSubCats = subCategories.filter(sc => form.category ? sc.parent_category === form.category : false);
  const showSubCat = SUB_CAT_CATEGORIES.includes(form.category);

  const resetForm = () => {
    setForm({ name: '', entity_type: tab, phone: '', email: '', address: '', category: '', sub_category: '', supplies: [] });
    setEditing(null);
    setFormBankAccounts([]);
    setNewBankAcc({ account_name: '', bank_name: '', account_number: '', ifsc_code: '' });
  };

  const addFormBankAccount = () => {
    if (!newBankAcc.account_name.trim()) return;
    setFormBankAccounts(prev => [...prev, { ...newBankAcc, _temp_id: Date.now() }]);
    setNewBankAcc({ account_name: '', bank_name: '', account_number: '', ifsc_code: '' });
  };

  const removeFormBankAccount = (idx) => {
    setFormBankAccounts(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let entityId;
      if (editing) {
        await entitiesApi.update(editing, form);
        entityId = editing;
      } else {
        const { data } = await entitiesApi.create(form);
        entityId = data._id;
      }
      // Create bank accounts for the entity
      for (const acc of formBankAccounts) {
        if (acc._temp_id) {
          // New account, not yet saved
          await entityBankAccountsApi.create({
            entity_id: entityId,
            account_name: acc.account_name,
            bank_name: acc.bank_name,
            account_number: acc.account_number,
            ifsc_code: acc.ifsc_code,
          });
        }
      }
      setShowForm(false);
      resetForm();
      fetchEntities();
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entity?')) return;
    await entitiesApi.delete(id);
    fetchEntities();
  };

  const openEdit = async (entity) => {
    setForm({
      name: entity.name, entity_type: entity.entity_type,
      phone: entity.phone || '', email: entity.email || '', address: entity.address || '',
      category: entity.category || '', sub_category: entity.sub_category || '',
      supplies: entity.supplies || []
    });
    setEditing(entity._id);
    // Load existing bank accounts
    try {
      const { data } = await entityBankAccountsApi.list(entity._id);
      setFormBankAccounts(data.map(a => ({ ...a, _existing: true })));
    } catch { setFormBankAccounts([]); }
    setShowForm(true);
  };

  // Bank accounts dialog for existing entity
  const openBankAccounts = async (entity) => {
    setShowBankAccounts(entity);
    try {
      const { data } = await entityBankAccountsApi.list(entity._id);
      setBankAccounts(data);
    } catch { setBankAccounts([]); }
  };

  const handleCreateDialogBankAccount = async () => {
    if (!dialogNewAcc.account_name.trim() || !showBankAccounts) return;
    try {
      await entityBankAccountsApi.create({
        entity_id: showBankAccounts._id,
        account_name: dialogNewAcc.account_name.trim(),
        bank_name: dialogNewAcc.bank_name,
        account_number: dialogNewAcc.account_number,
        ifsc_code: dialogNewAcc.ifsc_code,
      });
      const { data } = await entityBankAccountsApi.list(showBankAccounts._id);
      setBankAccounts(data);
      setDialogNewAcc({ account_name: '', bank_name: '', account_number: '', ifsc_code: '' });
    } catch {}
  };

  const handleDeleteBankAccount = async (accId) => {
    if (!window.confirm('Delete this bank account?')) return;
    try {
      await entityBankAccountsApi.delete(accId);
      setBankAccounts(prev => prev.filter(a => a._id !== accId));
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="entities-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Entities</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage persons, vendors, partners & customers</p>
        </div>
        {isWriter && (
          <Button className="gap-1.5" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-entity-btn">
            <Plus className="w-4 h-4" /> Add Entity
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="entity-type-tabs">
        <TabsList>
          {ENTITY_TYPES.map(t => (
            <TabsTrigger key={t.value} value={t.value} className="gap-1.5 text-xs" data-testid={`tab-${t.value}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
              <span className="ml-1 text-xs text-muted-foreground">({entities.filter(e => e.entity_type === t.value).length})</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : filteredEntities.length === 0 ? (
        <div className="bg-card border border-border p-12 text-center">
          <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
          <h3 className="font-semibold mb-1">No {ENTITY_TYPES.find(t => t.value === tab)?.label || 'entities'} yet</h3>
          <p className="text-sm text-muted-foreground">Add one to start tracking</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEntities.map(entity => (
            <div key={entity._id} className="bg-card border border-border p-5 space-y-3" data-testid={`entity-card-${entity._id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold tracking-tight">{entity.name}</h3>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider capitalize">{entity.entity_type}</span>
                </div>
                {isWriter && (
                  <div className="flex gap-1">
                    <button onClick={() => openBankAccounts(entity)} className="p-1.5 hover:bg-muted rounded" title="Bank Accounts" data-testid={`bank-accounts-${entity._id}`}>
                      <CreditCard className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => openEdit(entity)} className="p-1.5 hover:bg-muted rounded" data-testid={`edit-entity-${entity._id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(entity._id)} className="p-1.5 hover:bg-muted rounded text-red-600" data-testid={`delete-entity-${entity._id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
              {/* Category/Sub-cat tags + Supplies */}
              {((entity.category || entity.sub_category) || (entity.supplies && entity.supplies.length > 0)) && (
                <div className="flex gap-1.5 flex-wrap">
                  {entity.supplies && entity.supplies.length > 0 ? (
                    entity.supplies.map((s, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-muted text-muted-foreground">
                        <span className={`w-1.5 h-1.5 rounded-full ${CATEGORY_DOT_COLOR[s.category] || 'bg-gray-500'}`} />
                        {s.sub_category}
                      </span>
                    ))
                  ) : (
                    <>
                      {entity.category && (
                        <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground capitalize">{entity.category.replace('_', ' ')}</span>
                      )}
                      {entity.sub_category && (
                        <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground">{entity.sub_category}</span>
                      )}
                    </>
                  )}
                </div>
              )}
              {(entity.phone || entity.email || entity.address) && (
                <div className="space-y-1 text-sm text-muted-foreground pt-2 border-t border-border">
                  {entity.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" /> {entity.phone}</div>}
                  {entity.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> {entity.email}</div>}
                  {entity.address && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {entity.address}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ==================== ENTITY FORM DIALOG ==================== */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) resetForm(); setShowForm(v); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Entity' : 'Add Entity'}</DialogTitle>
            <DialogDescription className="sr-only">{editing ? 'Edit entity details' : 'Add a new entity'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="entity-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Name</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1.5" data-testid="entity-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Type</Label>
                <Select value={form.entity_type} onValueChange={v => setForm(f => ({ ...f, entity_type: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="entity-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Category & Sub-Category (legacy single) */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Primary Category</Label>
                <Select value={form.category || '__none__'} onValueChange={v => setForm(f => ({ ...f, category: v === '__none__' ? '' : v, sub_category: '' }))}>
                  <SelectTrigger className="mt-1.5" data-testid="entity-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- None --</SelectItem>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {showSubCat && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">{SUB_CAT_LABEL[form.category] || 'Sub-Type'}</Label>
                  <Select value={form.sub_category || '__none__'} onValueChange={v => setForm(f => ({ ...f, sub_category: v === '__none__' ? '' : v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="entity-sub-category-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- None --</SelectItem>
                      {filteredSubCats.map(sc => <SelectItem key={sc._id} value={sc.name}>{sc.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Multiple Supplies (Materials / Services) */}
            <div className="border border-border p-3 space-y-3" data-testid="supplies-section">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" /> Materials &amp; Services Supplied
                </Label>
                <span className="text-xs text-muted-foreground">{form.supplies.length} item{form.supplies.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Current supplies list */}
              {form.supplies.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {form.supplies.map((s, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-muted rounded" data-testid={`supply-tag-${idx}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.category === 'material' ? 'bg-blue-500' : 'bg-purple-500'}`} />
                      {s.sub_category}
                      <button type="button" onClick={() => setForm(f => ({ ...f, supplies: f.supplies.filter((_, i) => i !== idx) }))} className="ml-0.5 hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Add supply: pick from material/service sub-categories */}
              <div className="space-y-2">
                <Input
                  value={supplySearch}
                  onChange={e => setSupplySearch(e.target.value)}
                  placeholder="Search materials & services..."
                  className="h-7 text-xs"
                  data-testid="supply-search"
                />
                <div className="max-h-36 overflow-y-auto border border-border rounded p-2 space-y-0.5">
                  {subCategories.filter(sc => SUB_CAT_CATEGORIES.includes(sc.parent_category) && (!supplySearch || sc.name.toLowerCase().includes(supplySearch.toLowerCase()))).map(sc => {
                    const isSelected = form.supplies.some(s => s.category === sc.parent_category && s.sub_category === sc.name);
                    return (
                      <label key={sc._id} className="flex items-center gap-2 py-1 px-1.5 hover:bg-muted/50 rounded cursor-pointer text-xs" data-testid={`supply-option-${sc._id}`}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setForm(f => ({ ...f, supplies: [...f.supplies, { category: sc.parent_category, sub_category: sc.name }] }));
                            } else {
                              setForm(f => ({ ...f, supplies: f.supplies.filter(s => !(s.category === sc.parent_category && s.sub_category === sc.name)) }));
                            }
                          }}
                        />
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${CATEGORY_DOT_COLOR[sc.parent_category] || 'bg-gray-500'}`} />
                        <span className="flex-1">{sc.name}</span>
                        <span className="text-muted-foreground capitalize">{sc.parent_category}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Phone</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="mt-1.5" data-testid="entity-phone-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1.5" data-testid="entity-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Address</Label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="mt-1.5" data-testid="entity-address-input" />
            </div>

            {/* Bank Accounts Section */}
            <div className="border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5" /> Bank Accounts
                </Label>
                <span className="text-xs text-muted-foreground">{formBankAccounts.length} account{formBankAccounts.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Existing / added accounts */}
              {formBankAccounts.length > 0 && (
                <div className="space-y-1.5">
                  {formBankAccounts.map((acc, idx) => (
                    <div key={acc._id || acc._temp_id || idx} className="flex items-center justify-between bg-muted/50 p-2 text-xs" data-testid={`form-bank-acc-${idx}`}>
                      <div>
                        <span className="font-medium">{acc.account_name}</span>
                        {acc.bank_name && <span className="text-muted-foreground ml-1">({acc.bank_name})</span>}
                        {acc.account_number && <span className="text-muted-foreground ml-1">A/C: {acc.account_number}</span>}
                      </div>
                      {!acc._existing && (
                        <button type="button" onClick={() => removeFormBankAccount(idx)} className="p-1 hover:bg-muted rounded text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add new bank account inline */}
              <div className="grid grid-cols-2 gap-2">
                <Input value={newBankAcc.account_name} onChange={e => setNewBankAcc(a => ({ ...a, account_name: e.target.value }))} placeholder="Account name *" className="h-7 text-xs" data-testid="form-new-acc-name" />
                <Input value={newBankAcc.bank_name} onChange={e => setNewBankAcc(a => ({ ...a, bank_name: e.target.value }))} placeholder="Bank name" className="h-7 text-xs" data-testid="form-new-acc-bank" />
                <Input value={newBankAcc.account_number} onChange={e => setNewBankAcc(a => ({ ...a, account_number: e.target.value }))} placeholder="Account number" className="h-7 text-xs" data-testid="form-new-acc-number" />
                <Input value={newBankAcc.ifsc_code} onChange={e => setNewBankAcc(a => ({ ...a, ifsc_code: e.target.value }))} placeholder="IFSC code" className="h-7 text-xs" data-testid="form-new-acc-ifsc" />
              </div>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={addFormBankAccount} data-testid="form-add-bank-acc-btn">
                <PlusCircle className="w-3 h-3" /> Add Bank Account
              </Button>
            </div>

            <Button type="submit" className="w-full" data-testid="entity-submit-btn">{editing ? 'Update' : 'Add Entity'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== BANK ACCOUNTS DIALOG (existing entity) ==================== */}
      <Dialog open={!!showBankAccounts} onOpenChange={() => { setShowBankAccounts(null); setBankAccounts([]); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bank Accounts — {showBankAccounts?.name}</DialogTitle>
            <DialogDescription className="sr-only">Manage bank accounts for this entity</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="entity-bank-accounts-dialog">
            {isWriter && (
              <div className="border border-border p-3 space-y-3">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Add Bank Account</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={dialogNewAcc.account_name} onChange={e => setDialogNewAcc(a => ({ ...a, account_name: e.target.value }))} placeholder="Account name *" className="h-8 text-xs" data-testid="entity-acc-name" />
                  <Input value={dialogNewAcc.bank_name} onChange={e => setDialogNewAcc(a => ({ ...a, bank_name: e.target.value }))} placeholder="Bank name" className="h-8 text-xs" data-testid="entity-acc-bank" />
                  <Input value={dialogNewAcc.account_number} onChange={e => setDialogNewAcc(a => ({ ...a, account_number: e.target.value }))} placeholder="Account number" className="h-8 text-xs" data-testid="entity-acc-number" />
                  <Input value={dialogNewAcc.ifsc_code} onChange={e => setDialogNewAcc(a => ({ ...a, ifsc_code: e.target.value }))} placeholder="IFSC code" className="h-8 text-xs" data-testid="entity-acc-ifsc" />
                </div>
                <Button size="sm" onClick={handleCreateDialogBankAccount} data-testid="save-entity-bank-account-btn">Add Account</Button>
              </div>
            )}
            {bankAccounts.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <Landmark className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No bank accounts added yet
              </div>
            ) : (
              <div className="space-y-2">
                {bankAccounts.map(acc => (
                  <div key={acc._id} className="flex items-center justify-between border border-border p-3" data-testid={`bank-account-${acc._id}`}>
                    <div>
                      <div className="text-sm font-medium">{acc.account_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {acc.bank_name && <span>{acc.bank_name}</span>}
                        {acc.account_number && <span className="ml-2">A/C: {acc.account_number}</span>}
                        {acc.ifsc_code && <span className="ml-2">IFSC: {acc.ifsc_code}</span>}
                      </div>
                    </div>
                    {isWriter && (
                      <button onClick={() => handleDeleteBankAccount(acc._id)} className="p-1.5 hover:bg-muted rounded text-red-600" data-testid={`delete-bank-account-${acc._id}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
